/* eslint-disable max-lines -- Pre-existing violation */
import {isNil, isNotNil} from 'ramda';

import {DashboardAdaptiveDensityParticipant} from '../types/DashboardAdaptiveDensityParticipant';
import {toDashboardAdaptiveCssHeight} from './toDashboardAdaptiveCssHeight';

const GEOMETRY_TOLERANCE = 0.5;
const ADAPTIVE_ROW_SELECTOR = '[data-dashboard-adaptive-row-index]';
const PROBE_ROW_SELECTOR = '[data-dashboard-adaptive-probe-index]';
const TILE_SELECTOR = '[data-dashboard-tile-id]';

const isStableGeometry = (first: number | null, second: number) =>
  isNotNil(first) && Math.abs(first - second) <= GEOMETRY_TOLERANCE;

const nextFrame = () =>
  new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

interface RowCostMeasurement {
  height: number;
  cost: number;
}

interface ObservedAdaptiveRow {
  rowIndex: number;
  height: number;
}

interface RegisteredParticipant {
  participant: DashboardAdaptiveDensityParticipant;
  naturalRowsObserver: ResizeObserver;
  adaptiveRowsObserver: ResizeObserver;
  observedNaturalRows: Element | null;
  observedNaturalHeight: number | null;
  observedAdaptiveRows: Map<Element, ObservedAdaptiveRow>;
  rowCosts: Map<number, RowCostMeasurement>;
  rowCostSignature: string;
}

interface AllocationPlan {
  entry: RegisteredParticipant;
  baselineBudget: number;
  completionRows: number;
  contentHeight: number;
}

export class DashboardAdaptiveDensityCoordinator {
  private root: HTMLElement | null = null;
  private rootObserver: ResizeObserver | null = null;
  private participants = new Map<string, RegisteredParticipant>();
  private observedTiles = new Set<Element>();
  private observedTileRects = new Map<Element, DOMRect>();
  private expectedTileHeights = new Map<Element, number>();
  private expectedTileWidths = new Map<Element, number>();
  private expectedRootWidth: number | null = null;
  private expectedRootHeight: number | null = null;
  private observedRootRect: DOMRect | null = null;
  private layoutSignature = '';
  private generation = 0;
  private scheduledFrame: number | null = null;
  private isRunning = false;
  private isDragging = false;
  private draggingListeners = new Set<VoidFunction>();
  private hasDeferredFocusShrink = false;
  private hasPendingExternalInvalidation = false;

  setRoot = (root: HTMLElement | null) => {
    if (this.root === root) {
      return;
    }

    this.generation += 1;
    if (isNotNil(this.scheduledFrame)) {
      window.cancelAnimationFrame(this.scheduledFrame);
      this.scheduledFrame = null;
    }
    this.rootObserver?.disconnect();
    this.root?.removeEventListener('focusout', this.handleFocusOut, true);
    document.fonts?.removeEventListener('loadingdone', this.handleFontsLoaded);
    this.root = root;
    this.observedTiles.clear();
    this.observedTileRects.clear();
    this.expectedTileHeights.clear();
    this.expectedTileWidths.clear();
    this.expectedRootWidth = null;
    this.expectedRootHeight = null;
    this.observedRootRect = null;

    if (isNil(root)) {
      return;
    }

    this.rootObserver = new ResizeObserver(this.handleRootResize);
    this.observedRootRect = root.getBoundingClientRect();
    this.rootObserver.observe(root);
    root.addEventListener('focusout', this.handleFocusOut, true);
    document.fonts?.addEventListener('loadingdone', this.handleFontsLoaded);
    this.refreshObservedTiles();
    this.invalidate();
  };

  setLayoutSignature(signature: string) {
    if (this.layoutSignature === signature) {
      return;
    }

    this.layoutSignature = signature;
    this.refreshObservedTiles();
    this.invalidate();
  }

  setDragging(isDragging: boolean) {
    if (this.isDragging === isDragging) {
      return;
    }

    this.isDragging = isDragging;
    this.generation += 1;
    this.draggingListeners.forEach((listener) => listener());

    if (!isDragging) {
      this.invalidate();
    }
  }

  getIsDragging = () => this.isDragging;

  subscribeDragging = (listener: VoidFunction) => {
    this.draggingListeners.add(listener);

    return () => this.draggingListeners.delete(listener);
  };

  register(participant: DashboardAdaptiveDensityParticipant) {
    const existing = this.participants.get(participant.id);

    if (isNotNil(existing)) {
      existing.participant = participant;
      this.updateParticipantObservers(existing);
      this.invalidate();
      return () => this.unregister(participant.id, existing);
    }

    const entry: RegisteredParticipant = {
      participant,
      naturalRowsObserver: new ResizeObserver(() => this.handleNaturalRowsResize(entry)),
      adaptiveRowsObserver: new ResizeObserver(() => this.handleAdaptiveRowsResize(entry)),
      observedNaturalRows: null,
      observedNaturalHeight: null,
      observedAdaptiveRows: new Map(),
      rowCosts: new Map(),
      rowCostSignature: '',
    };
    this.participants.set(participant.id, entry);
    this.updateParticipantObservers(entry);
    this.refreshObservedTiles();
    this.invalidate();

    return () => this.unregister(participant.id, entry);
  }

  update(participant: DashboardAdaptiveDensityParticipant) {
    const entry = this.participants.get(participant.id);

    if (isNil(entry)) {
      return;
    }

    const didLayoutChange = entry.participant.layoutSignature !== participant.layoutSignature;
    entry.participant = participant;
    this.updateParticipantObservers(entry);

    if (didLayoutChange) {
      entry.rowCosts.clear();
      entry.rowCostSignature = '';
      this.invalidate();
    }
  }

  private unregister(id: string, entry: RegisteredParticipant) {
    if (this.participants.get(id) !== entry) {
      return;
    }

    entry.naturalRowsObserver.disconnect();
    entry.adaptiveRowsObserver.disconnect();
    entry.observedAdaptiveRows.clear();
    this.participants.delete(id);
    this.invalidate();
  }

  private updateParticipantObservers(entry: RegisteredParticipant) {
    entry.adaptiveRowsObserver.disconnect();
    entry.observedAdaptiveRows.clear();
    const elements = entry.participant.getElements();
    let didInvalidateCachedRow = false;

    if (entry.observedNaturalRows !== elements.naturalRows) {
      entry.naturalRowsObserver.disconnect();
      entry.observedNaturalRows = elements.naturalRows;
      entry.observedNaturalHeight = elements.naturalRows?.getBoundingClientRect().height ?? null;
      if (isNotNil(elements.naturalRows)) {
        entry.naturalRowsObserver.observe(elements.naturalRows);
      }
    }
    elements.region
      ?.querySelectorAll(`${ADAPTIVE_ROW_SELECTOR}, ${PROBE_ROW_SELECTOR}`)
      .forEach((row) => {
        const rowIndex = this.getObservedRowIndex(row);
        if (isNil(rowIndex)) {
          return;
        }
        const height = row.getBoundingClientRect().height;
        const cachedMeasurement = entry.rowCosts.get(rowIndex);
        if (isNotNil(cachedMeasurement) && !isStableGeometry(cachedMeasurement.height, height)) {
          entry.rowCosts.delete(rowIndex);
          didInvalidateCachedRow = true;
        }
        entry.observedAdaptiveRows.set(row, {rowIndex, height});
        entry.adaptiveRowsObserver.observe(row);
      });

    if (didInvalidateCachedRow) {
      this.invalidate();
    }
  }

  private getObservedRowIndex(row: Element) {
    const element = row instanceof HTMLElement ? row : null;
    const value =
      element?.dataset.dashboardAdaptiveRowIndex ?? element?.dataset.dashboardAdaptiveProbeIndex;
    if (isNil(value)) {
      return null;
    }
    const rowIndex = Number(value);
    return Number.isNaN(rowIndex) ? null : rowIndex;
  }

  private handleNaturalRowsResize(entry: RegisteredParticipant) {
    const naturalRows = entry.observedNaturalRows;
    if (isNil(naturalRows)) {
      return;
    }
    const height = naturalRows.getBoundingClientRect().height;
    if (isStableGeometry(entry.observedNaturalHeight, height)) {
      return;
    }
    entry.observedNaturalHeight = height;
    this.invalidate();
  }

  private handleAdaptiveRowsResize(entry: RegisteredParticipant) {
    let didChange = false;
    entry.observedAdaptiveRows.forEach((observed, row) => {
      const height = row.getBoundingClientRect().height;
      if (isStableGeometry(observed.height, height)) {
        return;
      }
      observed.height = height;
      entry.rowCosts.delete(observed.rowIndex);
      didChange = true;
    });
    if (didChange) {
      this.invalidate();
    }
  }

  private handleFontsLoaded = () => {
    this.participants.forEach((entry) => {
      entry.rowCosts.clear();
      entry.rowCostSignature = '';
    });
    this.invalidate();
  };

  private handleFocusOut = () => {
    if (this.hasDeferredFocusShrink) {
      this.invalidate();
    }
  };

  private handleRootResize = (entries: ResizeObserverEntry[]) => {
    entries.forEach((entry) => {
      const rect = entry.target.getBoundingClientRect();
      if (entry.target === this.root) {
        const previous = this.observedRootRect;
        this.observedRootRect = rect;
        if (isNil(previous)) {
          return;
        }
        const widthChanged = !isStableGeometry(previous.width, rect.width);
        const heightChanged = !isStableGeometry(previous.height, rect.height);
        if (!widthChanged && !heightChanged) {
          return;
        }
        if (this.isRunning) {
          if (
            widthChanged ||
            (heightChanged && !isStableGeometry(this.expectedRootHeight, rect.height))
          ) {
            this.invalidate();
          }
          return;
        }
        if (
          !isStableGeometry(this.expectedRootWidth, rect.width) ||
          !isStableGeometry(this.expectedRootHeight, rect.height)
        ) {
          this.invalidate();
        }
        return;
      }

      const previous = this.observedTileRects.get(entry.target);
      this.observedTileRects.set(entry.target, rect);
      if (isNil(previous)) {
        return;
      }
      const widthChanged = !isStableGeometry(previous.width, rect.width);
      const heightChanged = !isStableGeometry(previous.height, rect.height);
      if (!widthChanged && !heightChanged) {
        return;
      }
      if (this.isRunning) {
        const expectedHeight = this.expectedTileHeights.get(entry.target) ?? null;
        if (widthChanged || (heightChanged && !isStableGeometry(expectedHeight, rect.height))) {
          this.invalidate();
        }
        return;
      }
      const expectedWidth = this.expectedTileWidths.get(entry.target) ?? null;
      const expectedHeight = this.expectedTileHeights.get(entry.target) ?? null;
      if (
        !isStableGeometry(expectedWidth, rect.width) ||
        !isStableGeometry(expectedHeight, rect.height)
      ) {
        this.invalidate();
      }
    });
  };

  private refreshObservedTiles() {
    if (isNil(this.root) || isNil(this.rootObserver)) {
      return;
    }

    const tiles = new Set(this.root.querySelectorAll(TILE_SELECTOR));
    this.observedTiles.forEach((tile) => {
      if (tiles.has(tile)) {
        return;
      }
      this.rootObserver?.unobserve(tile);
      this.observedTiles.delete(tile);
      this.observedTileRects.delete(tile);
      this.expectedTileHeights.delete(tile);
      this.expectedTileWidths.delete(tile);
    });
    tiles.forEach((tile) => {
      if (!this.observedTiles.has(tile)) {
        this.observedTileRects.set(tile, tile.getBoundingClientRect());
        this.rootObserver?.observe(tile);
        this.observedTiles.add(tile);
      }
    });
  }

  private hasUnobservedExternalGeometry() {
    let didChange = false;
    const rootRect = this.root?.getBoundingClientRect();
    if (
      isNotNil(rootRect) &&
      isNotNil(this.observedRootRect) &&
      (!isStableGeometry(this.observedRootRect.width, rootRect.width) ||
        !isStableGeometry(this.observedRootRect.height, rootRect.height))
    ) {
      this.observedRootRect = rootRect;
      didChange = true;
    }

    this.observedTiles.forEach((tile) => {
      const rect = tile.getBoundingClientRect();
      const observedRect = this.observedTileRects.get(tile);
      if (
        isNotNil(observedRect) &&
        isStableGeometry(observedRect.width, rect.width) &&
        isStableGeometry(observedRect.height, rect.height)
      ) {
        return;
      }
      this.observedTileRects.set(tile, rect);
      if (isNotNil(observedRect)) {
        didChange = true;
      }
    });

    if (didChange) {
      this.invalidate();
    }
    return didChange;
  }

  private invalidate() {
    this.generation += 1;
    if (this.isRunning) {
      this.hasPendingExternalInvalidation = true;
      return;
    }
    this.scheduleRun();
  }

  private scheduleRun() {
    if (this.isDragging || isNotNil(this.scheduledFrame)) {
      return;
    }
    this.scheduledFrame = window.requestAnimationFrame(() => {
      this.scheduledFrame = null;
      void this.run(this.generation);
    });
  }

  private isStale(generation: number) {
    return generation !== this.generation || this.isDragging || isNil(this.root);
  }

  // eslint-disable-next-line max-statements -- Pre-existing violation
  private async run(generation: number) {
    if (this.isRunning || this.isStale(generation) || this.participants.size === 0) {
      return;
    }

    this.isRunning = true;
    this.hasDeferredFocusShrink = false;
    this.refreshObservedTiles();

    try {
      const entries = Array.from(this.participants.values()).filter((entry) =>
        isNotNil(entry.participant.getElements().region)
      );
      const previousAllocations = new Map<RegisteredParticipant, string>();

      entries.forEach((entry) => {
        const region = entry.participant.getElements().region;
        if (isNil(region)) {
          return;
        }
        previousAllocations.set(
          entry,
          region.style.getPropertyValue('--dashboard-adaptive-min-height')
        );
        region.style.setProperty('--dashboard-adaptive-min-height', '0rem');
      });

      this.root?.getBoundingClientRect();
      const baselineBudgets = new Map(
        entries.flatMap((entry) => {
          const region = entry.participant.getElements().region;
          return isNil(region) ? [] : [[entry, region.getBoundingClientRect().height] as const];
        })
      );

      entries.forEach((entry) => {
        const region = entry.participant.getElements().region;
        const previousAllocation = previousAllocations.get(entry);
        if (isNil(region)) {
          return;
        }
        if (isNil(previousAllocation) || previousAllocation.length === 0) {
          region.style.removeProperty('--dashboard-adaptive-min-height');
        } else {
          region.style.setProperty('--dashboard-adaptive-min-height', previousAllocation);
        }
      });

      if (this.isStale(generation)) {
        return;
      }

      const plans: AllocationPlan[] = [];
      for (const entry of entries) {
        const baselineBudget = baselineBudgets.get(entry) ?? 0;
        const plan = await this.createCompletionPlan(entry, baselineBudget, generation);
        if (this.isStale(generation)) {
          return;
        }
        plans.push(plan);
      }

      if (plans.some((plan) => this.wouldRemoveFocusedRow(plan))) {
        this.hasDeferredFocusShrink = true;
        return;
      }

      if (this.hasUnobservedExternalGeometry()) {
        return;
      }

      plans.forEach((plan) => {
        plan.entry.participant.setVisibleRows(plan.completionRows);
        plan.entry.participant.setProbeRow(plan.completionRows);
        this.setAllocationHeight(plan.entry, plan.contentHeight);
      });
      this.captureExpectedGeometry();

      await nextFrame();
      await nextFrame();
      if (this.isStale(generation)) {
        return;
      }

      const finalBudgets = new Map(
        plans.flatMap((plan) => {
          const region = plan.entry.participant.getElements().region;
          return isNil(region)
            ? []
            : [[plan.entry, region.getBoundingClientRect().height] as const];
        })
      );

      let didRefill = true;
      while (didRefill && !this.isStale(generation)) {
        didRefill = false;

        for (const plan of plans) {
          const participant = plan.entry.participant;
          const visibleRows = participant.getVisibleRows();
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (visibleRows >= participant.totalRows) {
            continue;
          }

          const nextCost = await this.getRowCost(plan.entry, visibleRows, generation);
          const finalBudget = finalBudgets.get(plan.entry) ?? 0;
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (isNil(nextCost) || plan.contentHeight + nextCost > finalBudget + GEOMETRY_TOLERANCE) {
            continue;
          }

          plan.contentHeight += nextCost;
          participant.setVisibleRows(visibleRows + 1);
          participant.setProbeRow(visibleRows + 1);
          didRefill = true;
        }

        if (didRefill) {
          await nextFrame();
        }
      }

      await nextFrame();
      if (!this.isStale(generation)) {
        this.captureExpectedGeometry();
      }
    } finally {
      this.isRunning = false;
      const shouldReconcile = this.hasPendingExternalInvalidation || generation !== this.generation;
      this.hasPendingExternalInvalidation = false;
      if (!this.isDragging && shouldReconcile) {
        this.scheduleRun();
      }
    }
  }

  private async createCompletionPlan(
    entry: RegisteredParticipant,
    baselineBudget: number,
    generation: number
  ): Promise<AllocationPlan> {
    const participant = entry.participant;
    let contentHeight = 0;
    let fittedRows = participant.naturalRows;

    while (fittedRows < participant.totalRows && !this.isStale(generation)) {
      const rowCost = await this.getRowCost(entry, fittedRows, generation);
      if (isNil(rowCost) || contentHeight + rowCost > baselineBudget + GEOMETRY_TOLERANCE) {
        break;
      }

      contentHeight += rowCost;
      fittedRows += 1;
    }

    const baselineRemainder = baselineBudget - contentHeight;
    if (
      fittedRows >= participant.totalRows ||
      baselineRemainder <= GEOMETRY_TOLERANCE ||
      this.isStale(generation)
    ) {
      return {entry, baselineBudget, completionRows: fittedRows, contentHeight};
    }

    const frontierCost = await this.getRowCost(entry, fittedRows, generation);
    if (isNil(frontierCost)) {
      return {entry, baselineBudget, completionRows: fittedRows, contentHeight};
    }

    return {
      entry,
      baselineBudget,
      completionRows: fittedRows + 1,
      contentHeight: contentHeight + frontierCost,
    };
  }

  private async getRowCost(
    entry: RegisteredParticipant,
    rowIndex: number,
    generation: number
  ): Promise<number | null> {
    const participant = entry.participant;
    const region = participant.getElements().region;
    const rowLayoutKey = participant.rowLayoutKeys[rowIndex];

    if (isNil(region) || isNil(rowLayoutKey)) {
      return null;
    }

    const width = region.getBoundingClientRect().width;
    const rowCostSignature = `${participant.layoutSignature}:${Math.round(width * 2) / 2}`;
    if (entry.rowCostSignature !== rowCostSignature) {
      entry.rowCosts.clear();
      entry.rowCostSignature = rowCostSignature;
    }

    const visibleRowSelector = `${ADAPTIVE_ROW_SELECTOR}[data-dashboard-adaptive-row-index="${rowIndex}"]`;
    const probeRowSelector = `${PROBE_ROW_SELECTOR}[data-dashboard-adaptive-probe-index="${rowIndex}"]`;
    const cachedMeasurement = entry.rowCosts.get(rowIndex);
    const currentlyRenderedRow =
      region.querySelector<HTMLElement>(visibleRowSelector) ??
      region.querySelector<HTMLElement>(probeRowSelector);
    if (
      isNotNil(cachedMeasurement) &&
      (isNil(currentlyRenderedRow) ||
        isStableGeometry(
          cachedMeasurement.height,
          currentlyRenderedRow.getBoundingClientRect().height
        ))
    ) {
      return cachedMeasurement.cost;
    }
    entry.rowCosts.delete(rowIndex);

    let row = region.querySelector<HTMLElement>(visibleRowSelector);

    if (isNil(row)) {
      if (participant.getProbeRow() !== rowIndex) {
        participant.setProbeRow(rowIndex);
        await nextFrame();
        if (this.isStale(generation)) {
          return null;
        }
      }
      row = region.querySelector<HTMLElement>(probeRowSelector);
    }

    if (isNil(row)) {
      return null;
    }

    const rowHeight = row.getBoundingClientRect().height;
    const adaptiveRows = participant.getElements().adaptiveRows;
    if (isNil(adaptiveRows)) {
      return null;
    }
    const adaptiveRowsStyle = window.getComputedStyle(adaptiveRows);
    const spacing = Number.parseFloat(
      rowIndex === participant.naturalRows ? adaptiveRowsStyle.paddingTop : adaptiveRowsStyle.rowGap
    );
    const cost = rowHeight + (Number.isNaN(spacing) ? 0 : spacing);
    entry.rowCosts.set(rowIndex, {height: rowHeight, cost});
    return cost;
  }

  private wouldRemoveFocusedRow(plan: AllocationPlan) {
    const participant = plan.entry.participant;
    if (plan.completionRows >= participant.getVisibleRows()) {
      return false;
    }

    const region = participant.getElements().region;
    const activeElement = document.activeElement;
    if (isNil(region) || isNil(activeElement) || !region.contains(activeElement)) {
      return false;
    }

    const focusedRow = activeElement.closest<HTMLElement>(ADAPTIVE_ROW_SELECTOR);
    const focusedRowIndex = Number(focusedRow?.dataset.dashboardAdaptiveRowIndex);
    return Number.isNaN(focusedRowIndex) || focusedRowIndex >= plan.completionRows;
  }

  private setAllocationHeight(entry: RegisteredParticipant, height: number) {
    const region = entry.participant.getElements().region;
    region?.style.setProperty(
      '--dashboard-adaptive-min-height',
      toDashboardAdaptiveCssHeight(height)
    );
  }

  private captureExpectedGeometry() {
    const rootRect = this.root?.getBoundingClientRect();
    this.expectedRootWidth = rootRect?.width ?? null;
    this.expectedRootHeight = rootRect?.height ?? null;
    this.observedRootRect = rootRect ?? null;
    this.expectedTileHeights.clear();
    this.expectedTileWidths.clear();
    this.observedTiles.forEach((tile) => {
      const rect = tile.getBoundingClientRect();
      this.expectedTileHeights.set(tile, rect.height);
      this.expectedTileWidths.set(tile, rect.width);
      this.observedTileRects.set(tile, rect);
    });
  }
}
